import React, { Fragment, useContext } from 'react';
import { scaleLinear } from 'd3-scale';
import { timeHour, timeMinute } from 'd3-time';
import { max } from 'd3-array';

import AGPGraph from './AGPGraph';
import AGPLabels from './AGPLabels';
import BasalGraphArea from './BasalGraphArea';
import BaseSVG from './BaseSVG';
import BolusHexBin from './BolusHexBin';
import BolusScatterPlot from './BolusScatterPlot';
import D3Component from './D3Component';
import GlucoseSVG from './GlucoseSVG';
import MainView from './MainView';
import { parseDate } from '../utils/formats';
import YAxis from './YAxis';
import './Dashboard.css';
import PresentationContext from '../PresentationContext';
import { Math } from 'core-js';

const debug = (process.env.NODE_ENV !== 'production') && false;

const bolusPlot = BolusHexBin;

function pointyHexagon(radius = 5) {
  const thirdPi = Math.PI / 3;
  const angles = [0, thirdPi, 2 * thirdPi, 3 * thirdPi, 4 * thirdPi, 5 * thirdPi];
  let x0 = 0;
  let y0 = 0;
  const points = angles.map(angle => {
    const x1 = -Math.sin(angle) * radius;
    const y1 = Math.cos(angle) * radius;
    const dx = x1 - x0;
    const dy = y1 - y0;
    x0 = x1;
    y0 = y1;
    return [dx, dy];
  });
  return `m${points.join('l')}z`;
}

function flatHexagon(r = 5) {
  const v = -Math.cos(60) * r;
  // return `m${-r},0 l${r / 2},${-v} h${r} l${r / 2},${-v} l${-r / 2},${-v} h${-r} z`;
  return `m${-r},0 l${r / 2},${-v} h${r} l${r / 2},${v} ${-r / 2},${v} h${-r} z`;
}


function InsulinReport(props) {
  const {
    days,
    sgvData = {},
    basalData = {},
    formatDateRange,
    formatGlucoseValue,
    interactive = true,
    percentile,
    profiles = {},
    profilesData = {},
    t,
  } = props;
  const firstDay = parseDate(Object.keys(sgvData).sort()[0]);
  if (debug) {
    console.log('Basal data in Insulin Report', basalData);
  }
  const {profileUseTimes, totalMinutes} = profilesData

  function calculateHourDifference(start, end) {
    const [startHour, startMinute] = start.split(':').map(Number);
    const [endHour, endMinute] = end.split(':').map(Number);
    
    const totalStartMinutes = startHour * 60 + startMinute;
    const totalEndMinutes = endHour * 60 + endMinute;
    
    const diffInMinutes = totalEndMinutes - totalStartMinutes;
    const hours = diffInMinutes / 60;
    
    return hours;
}

const parseTimeToDay = (time) => { // e.g. '05:00'
  const [hours, minutes] = time.split(':')
  const d = new Date();
  d.setHours(0,0,0,0);
  let newTime = timeHour.offset(d, parseInt(hours))
  newTime = timeMinute.offset(newTime, parseInt(minutes))
  return newTime
}

  function getTotalDailyDose(basal) {
    let tdd = 0
    for (let i = 0; i < basal.length; i++) {
      let hourDiff
      if(i < basal.length-1){
        hourDiff = calculateHourDifference(basal[i].time, basal[i+1].time)
      }else{
        hourDiff = calculateHourDifference(basal[0].time, basal[i].time)
      }
      tdd += parseFloat(basal[i].value) * hourDiff
    }
    return tdd
  }
  const {
    formatTimeString,
    timeFormat,
    durationFormat
  } = useContext(PresentationContext);
  const stepTimeFormat = d => (timeFormat(formatTimeString)(d))

  let maxBasalProfile
  for (const profileKey in profiles) {
      const profile = profiles[profileKey];
      const maxBasal = max(profile.profile.basal.map(b => b.value))
      maxBasalProfile = max([maxBasal, maxBasalProfile])
      profiles[profileKey]['percentageTime']=calcPercentageBasalUsed(profileKey)
  }

  const arrayOfObjects = Object.entries(profiles).map(([key, value]) => ({ key, ...value }));
  arrayOfObjects.sort((a, b) => (b.percentageTime || 0) - (a.percentageTime || 0));
  const sortedProfiles = arrayOfObjects.reduce((acc, curr) => {
    acc[curr.key] = curr;
    return acc;
  }, {});
  maxBasalProfile = Math.ceil(maxBasalProfile);
  const basalGraphHeight = 170;

  function calcTotalTime(){
    return Object.values(profileUseTimes).reduce((prev, curr) =>{
      return prev + curr
    },0)
  }

  function calcPercentageBasalUsed(profileId){
      const totalTime = calcTotalTime()
      const totalTimeProfile = profileUseTimes[profileId]
    return totalTimeProfile/totalTime;
  }

  function calcBasalPercentageTime(i) {
    const percentage = (profileUseTimes[Object.keys(profileUseTimes)[i]] / totalMinutes * 100).toFixed(1)
    return percentage
  }
  
  return (
    <MainView
      description={t('insulin.main.description')}
      heading={t('insulin.main.heading')}
      interactive={interactive}
      key="mainView"
      title={`Sensotrend Insulin Report ${formatDateRange(days)}`}
    >
      <section>
        <GlucoseSVG
          key="mainGraph"
          times={[firstDay, timeHour.offset(firstDay, 24)]}
          yLabel={<AGPLabels t={t} />}
        >
          <D3Component graph={AGPGraph} data={sgvData} formatGlucoseValue={formatGlucoseValue} percentile={percentile} t={t} />
        </GlucoseSVG>
      </section>
      <h2 style={{pageBreakBefore:'always'}}>Basal</h2>
        <p style={{transform:'rotate(0.75turn) translate(-150px) translateY(-10px)', fontSize:'0.8rem', width:'fit-content', height:'0'}}>{`${t('insulin.main.basalDoses')} (${t('insulin.iu/h')})`}</p>
            {Object.values(sortedProfiles).map((p, i) => {
              return(
                <section  key={`basalGraph45d6dd ${i}`}>
                  <div style={{'display':'flex'}}>
                    <BaseSVG
                    profile={p} 
                    yDomain={[maxBasalProfile,0]}
                    height={basalGraphHeight}
                    tickFormat={stepTimeFormat}
                    xTicks={p.profile.basal.map(t => parseTimeToDay(t.time))}
                    yAxis={
                    <YAxis
                      scale={scaleLinear().domain([0, maxBasalProfile]).range([basalGraphHeight, 0])}
                      tickFormat={f => ((isNaN(f) || (f % 1)) ? undefined : f.toFixed(2))}
                      tickCount={maxBasalProfile}
                    />
                    }
                    yLabel={
                      <g className="scatterplot bolus labels" transform="translate(10, 0)">
                          <Fragment>
                            <foreignObject x="0" y="0" width="60" height="200">
                              <div>
                                <p style={{fontSize:'0.7rem', position:'absolute'}}>{p.name}</p>
                                <p style={{fontSize:'0.9rem', position:'absolute', bottom: `${basalGraphHeight/2 + 20}px`, marginBottom:'10px'}}>{`${calcBasalPercentageTime(i)} %`}</p>
                                <p style={{fontSize:'0.7rem', position:'absolute', bottom: `${basalGraphHeight/2}px`, marginBottom:'7px'}}>{durationFormat(profileUseTimes[Object.keys(profileUseTimes)[i]])}</p>
                                <p style={{fontSize:'0.9rem', position:'absolute', bottom: '19px', 'fontWeight': 'bold'}}>{getTotalDailyDose(p.profile.basal).toFixed(0)} {t('insulin.iu/d')}</p>
                              </div> 
                            </foreignObject>
                          </Fragment>
                        </g>
                    }
                  >
                    <D3Component key={`basalGraph`} data={basalData} profile={p} index={1} maxYValue={maxBasalProfile} graphHeight={basalGraphHeight} graph={BasalGraphArea} labels={true} opacity={'1'} color={'#6a88b5'} t={t} />
                  </BaseSVG>
                  </div>
          </section>
        )
      })}
    </MainView>
  );
}

export default InsulinReport;
